import React from "react";
import { Check, Gift, Plus, Sparkles, X } from "lucide-react";

export const UpgradePopup = ({ pickedProducts, onYes, onNo }) => (
  <div
    className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-4 gg-fade-in"
    style={{ background: "rgba(46,26,33,0.45)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}
    onClick={onNo}
    data-testid="bundle-upgrade-popup"
  >
    <div
      onClick={(e) => e.stopPropagation()}
      className="gg-sheet-up relative w-full sm:max-w-[480px] rounded-t-[28px] sm:rounded-[28px] px-6 pt-7 pb-6 sm:p-8 text-center overflow-hidden"
      style={{
        background: "linear-gradient(160deg, rgba(255,250,246,0.82) 0%, rgba(251,234,240,0.72) 100%)",
        backdropFilter: "blur(24px) saturate(140%)",
        WebkitBackdropFilter: "blur(24px) saturate(140%)",
        border: "1px solid rgba(255,255,255,0.7)",
        boxShadow: "0 40px 80px -30px rgba(46,26,33,0.6), inset 0 1px 0 rgba(255,255,255,0.9)",
      }}
    >
      <span className="pointer-events-none absolute -top-24 -right-20 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(244,213,141,0.55), transparent 70%)" }} />
      <span className="pointer-events-none absolute -bottom-24 -left-20 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, rgba(212,83,126,0.25), transparent 70%)" }} />
      <span className="sm:hidden mx-auto mb-4 block w-10 h-1 rounded-full bg-plum/20" />
      <button
        onClick={onNo}
        className="absolute top-4 right-4 w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/60 transition-colors"
        aria-label="Close"
        data-testid="bundle-upgrade-close"
      >
        <X size={16} className="text-plum/60" />
      </button>

      <div className="relative">
        <p className="text-[10px] tracking-[0.4em] uppercase font-semibold text-gold inline-flex items-center gap-2">
          <Sparkles size={12} /> One step from Dream
        </p>
        <h3 className="font-serif text-plum mt-2 leading-[1.05]" style={{ fontSize: "clamp(26px, 6vw, 32px)" }}>
          Make it your <span className="italic text-blush">Dream Box</span>
        </h3>
        <p className="mt-2 font-serif italic text-plum/80 text-[15px]">
          Add 2 more pieces for just <span className="font-semibold not-italic">₹400 extra</span> and get an assured{" "}
          <span className="font-semibold not-italic">free gift</span>.
        </p>

        {/* Slot preview: 5 picked + 2 empty + gift */}
        <div className="mt-6 flex items-center justify-center gap-1 sm:gap-1.5 flex-nowrap" data-testid="bundle-upgrade-slots">
          {pickedProducts.slice(0, 5).map((p, i) => (
            <span
              key={p.id}
              className="gg-tray-pop flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full overflow-hidden ring-2 ring-white shadow-md"
              style={{ animationDelay: `${i * 70}ms` }}
            >
              <img src={p.image} alt="" className="w-full h-full object-cover" />
            </span>
          ))}
          {[0, 1].map((i) => (
            <span
              key={`e${i}`}
              className="gg-slot-pulse flex-shrink-0 w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center"
              style={{ border: "1.5px dashed #D4537E", background: "rgba(255,255,255,0.5)", animationDelay: `${i * 300}ms` }}
            >
              <Plus size={14} className="text-blush" />
            </span>
          ))}
          <span className="text-plum/40 text-sm px-0.5">+</span>
          <span
            className="gg-gift-glow-static flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center"
            style={{ background: "linear-gradient(135deg,#F4D58D,#C5985A)" }}
          >
            <Gift size={18} className="text-plum" />
          </span>
        </div>

        {/* Price compare */}
        <div
          className="mt-6 grid grid-cols-2 rounded-2xl overflow-hidden text-left"
          style={{ background: "rgba(255,255,255,0.5)", border: "1px solid rgba(255,255,255,0.8)" }}
        >
          <div className="px-4 py-3">
            <p className="text-[9px] tracking-[0.28em] uppercase font-semibold text-plum/50">Glam · 5 pcs</p>
            <p className="font-serif text-plum/50 text-[22px] leading-tight line-through decoration-1">₹799</p>
          </div>
          <div className="px-4 py-3" style={{ background: "linear-gradient(135deg,#4A2C35,#6B3E4C)" }}>
            <p className="text-[9px] tracking-[0.28em] uppercase font-semibold text-[#F4D58D]">Dream · 7 pcs + Gift</p>
            <p className="font-serif text-cream text-[22px] leading-tight">₹1,199</p>
          </div>
        </div>
        <p className="mt-2 text-[10px] tracking-[0.24em] uppercase font-semibold text-plum/60">
          Just ₹200 per extra piece · Gift is on us
        </p>

        <div className="mt-6 flex flex-col gap-2">
          <button
            onClick={onYes}
            data-testid="bundle-upgrade-yes"
            className="w-full py-3.5 rounded-full text-[11px] tracking-[0.28em] uppercase font-semibold text-cream transition-transform hover:-translate-y-0.5 active:scale-[0.98] inline-flex items-center justify-center gap-1.5"
            style={{ background: "linear-gradient(135deg,#4A2C35,#6B3E4C)", boxShadow: "0 14px 28px -12px rgba(74,44,53,0.7)" }}
          >
            Yes, add 2 more <Check size={14} />
          </button>
          <button
            onClick={onNo}
            data-testid="bundle-upgrade-no"
            className="w-full py-3 rounded-full text-[11px] tracking-[0.26em] uppercase font-semibold text-plum/70 hover:text-plum transition-colors"
          >
            Continue with 5 for ₹799
          </button>
        </div>
      </div>
    </div>
  </div>
);

export default UpgradePopup;
